import { useMemo, useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { ColumnDef } from '@tanstack/react-table'
import { Plus, Shapes, MoreHorizontal, Pencil, Archive, ArchiveRestore, Trash2, Lock, Layers, Rows3, Eye, EyeOff, Circle, CircleDot, Image as ImageIcon, AlertTriangle, Workflow } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { DataTable } from '@/components/data/DataTable'
import { useToast } from '@/hooks/use-toast'
import { handleApiError } from '@/lib/api/axios'
import { buildShapePath } from '@/lib/imagePaths'
import { classificationsApi, type Classification, type ClassificationInput, type ClassificationGraphic } from '../api'
import { ClassificationGraphicsDialog } from '../components/ClassificationGraphicsDialog'
import { ClassificationRulesDialog } from '../components/ClassificationRulesDialog'
import { EntityIcon } from '@/components/ui/entity-icon'
import { useCan } from '@/hooks/useCan'
import { startingRank } from '../startingRank'

/**
 * Admin screen for the classification model (replaces the Layers screen in the
 * new model). Lists the full taxonomy including retired rows.
 *
 * Shipped classifications (IsSystem) are VaultSentinel's, as shipped templates are.
 * In a customer's environment they are read-only: no edit, graphics, retire or
 * delete. Where shipped content is authored (SHIPPED_CONTENT_AUTHORING_ENABLED),
 * they change like any other, and a classification can be saved as shipped. A
 * customer's own classifications have full CRUD everywhere. The server enforces
 * all of it, and the classifications.* permission set. The screen is open to
 * every classifications.read; each change is offered only with its permission.
 */
/**
 * Every graphic a classification may draw, default first.
 *
 * Shown on the list rather than only inside the dialog because a classification with no default
 * renders a placeholder on the canvas for every object that matches nothing — invisible until
 * someone opens the visualisation and finds a gap where equipment should be.
 *
 * EntityIcon, not a raw <img>: hover-to-enlarge lives in that one component, and these renders
 * are unreadable at 32px. The enlarge is bigger than the icon default because equipment artwork
 * carries detail — port counts, drive bays — that a 96px preview loses.
 */
function GraphicsStrip({ items, emptyLabel }: { items: ClassificationGraphic[]; emptyLabel: string }) {
    if (!items.length) {
        return (
            <span className="flex items-center justify-end text-xs text-muted-foreground">
                {emptyLabel}
            </span>
        )
    }
    const MAX = 5
    const shown = items.slice(0, MAX)
    // Graphics assigned but none of them the default. Worth a marker on the list rather than
    // only inside the dialog: on the canvas every object here that matches no tags draws a
    // placeholder, and nothing else on this screen would say why.
    const noDefault = !items.some((g) => g.IsDefault)
    return (
        <span className="flex items-center justify-end gap-1.5">
            {noDefault && (
                <AlertTriangle
                    className="h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400"
                    aria-label="No default graphic"
                />
            )}
            {shown.map((g) => (
                <span
                    key={g.ShapeId}
                    className={
                        g.IsDefault
                            ? 'grid h-9 w-9 shrink-0 place-items-center rounded border border-primary/50 bg-background/60 p-0.5'
                            : 'grid h-9 w-9 shrink-0 place-items-center rounded border border-transparent p-0.5'
                    }
                    title={[g.ShapeFilename, g.ShapeTags].filter(Boolean).join(' — ')}
                >
                    <EntityIcon
                        src={g.ShapeFilename ? buildShapePath(g.ShapeFilename) : null}
                        alt={g.ShapeFilename || ''}
                        className="h-8 w-8"
                        enlargeSize={200}
                    />
                </span>
            ))}
            {items.length > MAX && (
                <span className="text-[11px] text-muted-foreground">+{items.length - MAX}</span>
            )}
        </span>
    )
}



export function ClassificationListPage() {
    const { t } = useTranslation()
    const { toast } = useToast()
    const queryClient = useQueryClient()
    // The screen is open to every classifications.read; a change is offered only where the API allows it.
    const canCreate = useCan('classifications.create')
    const canUpdate = useCan('classifications.update')
    const canDelete = useCan('classifications.delete')

    const [view, setView] = useState<'stack' | 'table'>(() => {
        try { return (localStorage.getItem('vs.classifications.view') as 'stack' | 'table') || 'stack' } catch { return 'stack' }
    })
    useEffect(() => {
        try { localStorage.setItem('vs.classifications.view', view) } catch { /* private mode */ }
    }, [view])
    // Retired rows stay in the model for history (objects and recovery
    // snapshots may reference them) but shouldn't clutter the working view.
    const [showRetired, setShowRetired] = useState(() => {
        try { return localStorage.getItem('vs.classifications.showRetired') === '1' } catch { return false }
    })
    useEffect(() => {
        try { localStorage.setItem('vs.classifications.showRetired', showRetired ? '1' : '0') } catch { /* private mode */ }
    }, [showRetired])
    const [editing, setEditing] = useState<Classification | null>(null)
    const [showForm, setShowForm] = useState(false)
    const [form, setForm] = useState<ClassificationInput>({ Name: '', Category: '', DisplayRank: 0 })

    // A deployment setting: it changes only with a redeploy, so it is asked once per page load.
    // Until it answers, shipped rows are treated as read-only; the server would refuse them anyway.
    const { data: authoring = false } = useQuery({
        queryKey: ['classification-authoring'],
        queryFn: async () => (await classificationsApi.authoring()).data.data.enabled === true,
        staleTime: Infinity,
    })
    /** A customer's own classification, or any classification where shipped content is authored. */
    const canChange = (c: Classification) => !c.IsSystem || authoring
    const [graphicsFor, setGraphicsFor] = useState<Classification | null>(null)
    const [rulesFor, setRulesFor] = useState<Classification | null>(null)

    // Every assignment in one request, indexed per classification. Forty-nine rows each asking
    // for their own would be forty-nine requests to render one table.
    const { data: graphics = [] } = useQuery({
        queryKey: ['classifications', 'graphics'],
        queryFn: async () => (await classificationsApi.graphics()).data.data,
    })
    const graphicsByClass = useMemo(() => {
        const m = new Map<number, ClassificationGraphic[]>()
        for (const g of graphics) {
            const list = m.get(g.ClassificationID) ?? []
            list.push(g)
            m.set(g.ClassificationID, list)
        }
        // Default first — the API already sorts this way, but the screen must not depend on the
        // wire order to decide which graphic leads.
        for (const list of m.values()) list.sort((a, b) => Number(b.IsDefault) - Number(a.IsDefault))
        return m
    }, [graphics])

    const { data: rows = [], isLoading } = useQuery({
        queryKey: ['classifications', 'admin'],
        queryFn: async () => {
            const r = await classificationsApi.adminList()
            return (r.data as any).data as Classification[]
        },
    })

    const invalidate = () => {
        queryClient.invalidateQueries({ queryKey: ['classifications'] })
    }
    const onError = (e: unknown) =>
        toast({ title: t('common.error', 'Error'), description: handleApiError(e), variant: 'destructive' })

    const saveMutation = useMutation({
        mutationFn: async () => {
            const { IsSystem, ...rest } = form
            const payload: ClassificationInput = authoring ? { ...rest, IsSystem: !!IsSystem } : rest
            if (editing) return classificationsApi.update(editing.id, payload)
            return classificationsApi.create(payload)
        },
        onSuccess: () => {
            invalidate()
            setShowForm(false)
            toast({ title: t('common.success', 'Success'), description: t('classifications.saved', 'Classification saved.') })
        },
        onError,
    })
    const activeMutation = useMutation({
        mutationFn: ({ id, active }: { id: number; active: boolean }) => classificationsApi.setActive(id, active),
        onSuccess: invalidate,
        onError,
    })
    const deleteMutation = useMutation({
        mutationFn: (id: number) => classificationsApi.remove(id),
        onSuccess: () => {
            invalidate()
            toast({ title: t('common.success', 'Success'), description: t('classifications.deleted', 'Classification deleted.') })
        },
        onError,
    })

    const openAdd = () => {
        setEditing(null)
        setForm({ Name: '', Category: '', DisplayRank: startingRank(rows), Description: '', AccessMode: '', IsSystem: false })
        setShowForm(true)
    }
    const openEdit = (c: Classification) => {
        setEditing(c)
        setForm({
            Name: c.Name,
            Category: c.Category,
            DisplayRank: c.DisplayRank,
            Description: c.Description ?? '',
            BackgroundColor: c.BackgroundColor ?? '',
            AccessMode: c.AccessMode ?? '',
            IsSystem: !!c.IsSystem,
        })
        setShowForm(true)
    }

    const retiredCount = useMemo(() => rows.filter((c) => !c.Active).length, [rows])
    // Categories offered when creating/editing. 'Uncategorised' is a legacy
    // artefact of the old layer rows — never something to file a new class under.
    const categoryOptions = useMemo(() => {
        const set = new Set(
            rows.map((c) => c.Category).filter((c): c is string => !!c && c !== 'Uncategorised')
        )
        return [...set].sort()
    }, [rows])
    /** Retired and referenced by nothing → safe to delete outright. */
    const isSafeToDelete = (c: Classification) => !c.Active && Number(c.ObjectCount ?? 0) === 0
    // What the two views actually render. Retired rows are hidden unless asked
    // for — they remain in the model either way.
    const visibleRows = useMemo(
        () => (showRetired ? rows : rows.filter((c) => c.Active)),
        [rows, showRetired]
    )

    // Categories ordered as they stack on the canvas: highest band first, and
    // each category's lanes highest-first inside it.
    const stackGroups = useMemo(() => {
        const byCat = new Map<string, Classification[]>()
        for (const c of visibleRows) {
            const cat = c.Category || 'Uncategorised'
            if (!byCat.has(cat)) byCat.set(cat, [])
            byCat.get(cat)!.push(c)
        }
        return [...byCat.entries()]
            .map(([category, items]) => ({
                category,
                items: [...items].sort((a, b) => Number(b.DisplayRank) - Number(a.DisplayRank)),
                top: Math.max(...items.map((i) => Number(i.DisplayRank) || 0)),
            }))
            .sort((a, b) => b.top - a.top)
    }, [visibleRows])

    const columns: ColumnDef<Classification>[] = useMemo(() => [
        {
            accessorKey: 'Name',
            header: t('classifications.name', 'Name'),
            cell: ({ row }) => (
                <span className="flex items-center gap-2">
                    <span
                        className="inline-block h-3 w-3 rounded-sm border"
                        style={{ background: row.original.BackgroundColor || 'transparent' }}
                    />
                    <span className={row.original.Active ? '' : 'text-muted-foreground line-through'}>
                        {row.original.Name}
                    </span>
                    {!!row.original.IsSystem && (
                        <Lock
                            className="h-3 w-3 text-muted-foreground"
                            aria-label={t('classifications.system', 'Shipped classification — maintained by VaultSentinel')}
                        />
                    )}
                </span>
            ),
        },
        { accessorKey: 'Category', header: t('classifications.category', 'Category') },
        { accessorKey: 'DisplayRank', header: t('classifications.band', 'Band (display rank)') },
        {
            accessorKey: 'ObjectCount',
            header: t('classifications.objects', 'Objects'),
        },
        {
            accessorKey: 'Active',
            header: t('classifications.status', 'Status'),
            cell: ({ row }) => row.original.Active
                ? <Badge variant="outline">{t('classifications.active', 'Active')}</Badge>
                : <Badge variant="secondary">{t('classifications.retired', 'Retired')}</Badge>,
        },
        {
            id: 'graphic',
            // Last content column and right-aligned, matching the stack view: the artwork reads
            // as a column down the page rather than as another field in the middle of the row.
            // Fixed width so the strip starts at the same x on every row whatever the name length.
            header: () => (
                <div className="w-[220px] text-right">{t('classifications.graphic', 'Graphics')}</div>
            ),
            cell: ({ row }) => (
                <div className="w-[220px]">
                    <GraphicsStrip
                        items={graphicsByClass.get(row.original.id) ?? []}
                        emptyLabel={t('classifications.graphicNone', 'None')}
                    />
                </div>
            ),
        },
        {
            id: 'actions',
            header: '',
            cell: ({ row }) => {
                const c = row.original
                return (
                    <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
                        {/* Every row has its dependency rules. Outside authoring, a shipped
                            classification offers nothing else: its rules to a customer's own
                            classifications are still the customer's to change. */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="h-8 w-8 p-0">
                                    <MoreHorizontal className="h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                {canChange(c) && (<>
                                {canUpdate && (<>
                                <DropdownMenuItem onClick={() => openEdit(c)}>
                                    <Pencil className="mr-2 h-4 w-4" />
                                    {t('common.edit', 'Edit')}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setGraphicsFor(c)}>
                                    <ImageIcon className="mr-2 h-4 w-4" />
                                    {t('classifications.graphicsAction', 'Graphics')}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => activeMutation.mutate({ id: c.id, active: !c.Active })}>
                                    {c.Active
                                        ? (<><Archive className="mr-2 h-4 w-4" />{t('classifications.retire', 'Retire')}</>)
                                        : (<><ArchiveRestore className="mr-2 h-4 w-4" />{t('classifications.reactivate', 'Reactivate')}</>)}
                                </DropdownMenuItem>
                                </>)}
                                {canDelete && (isSafeToDelete(c) || (!c.IsSystem && Number(c.ObjectCount ?? 0) === 0)) && (
                                    <DropdownMenuItem
                                        className="text-destructive"
                                        onClick={() => deleteMutation.mutate(c.id)}
                                    >
                                        <Trash2 className="mr-2 h-4 w-4" />
                                        {t('common.delete', 'Delete')}
                                    </DropdownMenuItem>
                                )}
                                </>)}
                                <DropdownMenuItem onClick={() => setRulesFor(c)}>
                                    <Workflow className="mr-2 h-4 w-4" />
                                    {t('classifications.rulesAction', 'Dependency rules')}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                )
            },
        },
    // Two answers arrive after the first render, and the cells read both: whether shipped content
    // is authored here (the menus), and the graphics (the graphics column). Memoised on t alone,
    // the table kept the first render's answer to each for good. Shipped rows never got their menu
    // where authoring is on, and the graphics column stayed empty. The permissions arrive late too
    // (/auth/me). The mutations and handlers left out are stable in what they do.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [t, authoring, graphicsByClass, canUpdate, canDelete])

    return (
        <div className="space-y-4 p-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Shapes className="h-5 w-5 text-primary" />
                    <div>
                        <h1 className="text-xl font-semibold">{t('classifications.title', 'Classifications')}</h1>
                        <p className="text-xs text-muted-foreground">
                            {t('classifications.subtitle', 'The technology-stack taxonomy: what kind of thing each object is, which band it draws on, and (via rules) what it may depend on. Shipped classifications come with the product and are maintained by VaultSentinel; add your own for anything specific to your estate.')}
                        </p>
                    </div>
                </div>
                {canCreate && (
                    <Button onClick={openAdd}>
                        <Plus className="mr-2 h-4 w-4" />
                        {t('classifications.add', 'Add Classification')}
                    </Button>
                )}
            </div>

            {/* Where shipped content is authored, say so: here shipped classifications change, and a
                customer's environment shows them read-only. */}
            {authoring && (
                <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200">
                    {t('classifications.authoringOn', 'Authoring is on in this environment: shipped classifications can be changed here, and new ones can be saved as shipped. Customers see shipped classifications read-only.')}
                </p>
            )}

            {/* Stack view mirrors the canvas: categories as bands, top of the
                technology stack first, one labelled lane per classification.
                A flat table fragments the taxonomy across pages and hides the
                one thing that matters here — the order things stack in. */}
            <div className="flex items-center gap-2">
                <Button
                    variant={view === 'stack' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setView('stack')}
                >
                    <Layers className="mr-2 h-4 w-4" />
                    {t('classifications.viewStack', 'Stack')}
                </Button>
                <Button
                    variant={view === 'table' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setView('table')}
                >
                    <Rows3 className="mr-2 h-4 w-4" />
                    {t('classifications.viewTable', 'Table')}
                </Button>
                <Button
                    variant={showRetired ? 'secondary' : 'outline'}
                    size="sm"
                    onClick={() => setShowRetired(!showRetired)}
                    title={t('classifications.retiredHint', 'Retired classifications stay for history: objects and recovery snapshots may still reference them.')}
                >
                    {showRetired ? <Eye className="mr-2 h-4 w-4" /> : <EyeOff className="mr-2 h-4 w-4" />}
                    {t('classifications.showRetired', 'Retired')}
                    {retiredCount > 0 && (
                        <span className="ml-2 rounded bg-muted px-1.5 text-[10px] font-semibold text-muted-foreground">
                            {retiredCount}
                        </span>
                    )}
                </Button>
                <span className="ml-2 text-xs text-muted-foreground">
                    {t('classifications.stackHint', 'Bands are drawn on the canvas in this order — highest at the top.')}
                </span>
            </div>

            {view === 'table' ? (
                <DataTable
                    columns={columns}
                    data={visibleRows}
                    searchKey="Name"
                    searchPlaceholder={t('classifications.search', 'Search classifications…')}
                    className={isLoading ? 'opacity-60' : ''}
                />
            ) : (
                <div className={`space-y-3 ${isLoading ? 'opacity-60' : ''}`}>
                    {stackGroups.map((group) => (
                        <div key={group.category} className="rounded-lg border">
                            <div className="flex items-center justify-between border-b bg-muted/40 px-3 py-2">
                                <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                                    {group.category}
                                </span>
                                <span className="text-[11px] text-muted-foreground">
                                    {t('classifications.laneCount', '{{lanes}} classifications · {{objects}} objects', {
                                        lanes: group.items.length,
                                        objects: group.items.reduce((n, c) => n + (Number(c.ObjectCount) || 0), 0),
                                    })}
                                </span>
                            </div>
                            <div>
                                {group.items.map((c) => (
                                    <div
                                        key={c.id}
                                        className="flex items-center gap-3 border-b px-3 py-2 last:border-0"
                                        style={{ background: `${c.BackgroundColor || 'transparent'}55` }}
                                    >
                                        {/* The row's information is narrowed to a flexible column so the
                                            graphics can hold a fixed strip on the right. Fixed rather than
                                            proportional: a strip that starts at the same x on every row
                                            reads as a column of artwork, and a proportional one ragged. */}
                                        <div className="flex min-w-0 flex-1 items-center gap-3">
                                        <span
                                            className="inline-block h-4 w-4 shrink-0 rounded-sm border"
                                            style={{ background: c.BackgroundColor || 'transparent' }}
                                        />
                                        <span className={`text-sm font-medium ${c.Active ? '' : 'text-muted-foreground line-through'}`}>
                                            {c.Name}
                                        </span>
                                        {!!c.IsSystem && (
                                            <Lock
                                                className="h-3 w-3 shrink-0 text-muted-foreground"
                                                aria-label={t('classifications.system', 'Shipped classification — maintained by VaultSentinel')}
                                            />
                                        )}
                                        {!c.Active && (
                                            <Badge variant="secondary" className="h-5">
                                                {t('classifications.retired', 'Retired')}
                                            </Badge>
                                        )}
                                        <span className="ml-auto text-xs text-muted-foreground">
                                            {t('classifications.bandShort', 'band')} {c.DisplayRank}
                                        </span>
                                        {Number(c.ObjectCount) > 0 ? (
                                            <Badge variant="outline" className="h-5 shrink-0 gap-1">
                                                <CircleDot className="h-3 w-3" />
                                                {t('classifications.inUse', 'In use · {{count}}', { count: Number(c.ObjectCount) })}
                                            </Badge>
                                        ) : (
                                            <Badge variant="secondary" className="h-5 shrink-0 gap-1 opacity-70">
                                                <Circle className="h-3 w-3" />
                                                {t('classifications.unused', 'Unused')}
                                            </Badge>
                                        )}
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" className="h-7 w-7 shrink-0 p-0">
                                                    <MoreHorizontal className="h-4 w-4" />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                                {canChange(c) && (<>
                                                {canUpdate && (<>
                                                <DropdownMenuItem onClick={() => openEdit(c)}>
                                                    <Pencil className="mr-2 h-4 w-4" />
                                                    {t('common.edit', 'Edit')}
                                                </DropdownMenuItem>
                                                <DropdownMenuItem onClick={() => setGraphicsFor(c)}>
                                                    <ImageIcon className="mr-2 h-4 w-4" />
                                                    {t('classifications.graphicsAction', 'Graphics')}
                                                </DropdownMenuItem>
                                                <DropdownMenuItem onClick={() => activeMutation.mutate({ id: c.id, active: !c.Active })}>
                                                    {c.Active
                                                        ? (<><Archive className="mr-2 h-4 w-4" />{t('classifications.retire', 'Retire')}</>)
                                                        : (<><ArchiveRestore className="mr-2 h-4 w-4" />{t('classifications.reactivate', 'Reactivate')}</>)}
                                                </DropdownMenuItem>
                                                </>)}
                                                {canDelete && (isSafeToDelete(c) || (!c.IsSystem && Number(c.ObjectCount ?? 0) === 0)) && (
                                                    <DropdownMenuItem className="text-destructive" onClick={() => deleteMutation.mutate(c.id)}>
                                                        <Trash2 className="mr-2 h-4 w-4" />
                                                        {t('common.delete', 'Delete')}
                                                    </DropdownMenuItem>
                                                )}
                                                </>)}
                                                <DropdownMenuItem onClick={() => setRulesFor(c)}>
                                                    <Workflow className="mr-2 h-4 w-4" />
                                                    {t('classifications.rulesAction', 'Dependency rules')}
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                        </div>

                                        <div className="w-[220px] shrink-0">
                                            <GraphicsStrip
                                                items={graphicsByClass.get(c.id) ?? []}
                                                emptyLabel={t('classifications.graphicNone', 'None')}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <ClassificationGraphicsDialog
                classification={graphicsFor}
                open={!!graphicsFor}
                onOpenChange={(o) => { if (!o) setGraphicsFor(null) }}
            />
            <ClassificationRulesDialog
                classification={rulesFor}
                classifications={rows}
                authoring={authoring}
                readOnly={!canUpdate}
                open={!!rulesFor}
                onOpenChange={(o) => { if (!o) setRulesFor(null) }}
            />

            {showForm && (
                <>
                    <div className="fixed inset-0 z-[199] bg-black/20" onClick={() => setShowForm(false)} aria-hidden />
                    <div className="fixed left-1/2 top-1/2 z-[200] w-[420px] max-w-[92vw] -translate-x-1/2 -translate-y-1/2 rounded-lg border bg-background p-4 shadow-xl space-y-4">
                        <h2 className="text-sm font-semibold">
                            {editing
                                ? t('classifications.editTitle', 'Edit Classification')
                                : t('classifications.addTitle', 'Add Classification')}
                        </h2>
                        <div className="space-y-1.5">
                            <Label htmlFor="cls-name">{t('classifications.name', 'Name')} *</Label>
                            <Input
                                id="cls-name"
                                value={form.Name}
                                onChange={(e) => setForm((f) => ({ ...f, Name: e.target.value }))}
                                maxLength={100}
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label htmlFor="cls-cat">{t('classifications.category', 'Category')} *</Label>
                                <Select
                                    value={form.Category}
                                    onValueChange={(v) => setForm((f) => ({ ...f, Category: v }))}
                                >
                                    <SelectTrigger id="cls-cat">
                                        <SelectValue placeholder={t('classifications.selectCategory', 'Select category')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {categoryOptions.map((cat) => (
                                            <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="cls-rank">{t('classifications.band', 'Band (display rank)')} *</Label>
                                <Input
                                    id="cls-rank"
                                    type="number"
                                    value={form.DisplayRank}
                                    onChange={(e) => setForm((f) => ({ ...f, DisplayRank: parseInt(e.target.value, 10) || 0 }))}
                                />
                            </div>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            {t('classifications.colourAuto', 'The band colour is generated automatically: each category owns a hue and its classifications are shades of it, ordered by band.')}
                        </p>
                        <div className="space-y-1.5">
                            <Label htmlFor="cls-desc">{t('classifications.description', 'Description')}</Label>
                            <Textarea
                                id="cls-desc"
                                rows={2}
                                value={form.Description ?? ''}
                                onChange={(e) => setForm((f) => ({ ...f, Description: e.target.value }))}
                                maxLength={1000}
                            />
                        </div>
                        {authoring && (
                            <div className="flex items-center gap-2">
                                <Checkbox
                                    id="cls-shipped"
                                    checked={!!form.IsSystem}
                                    onCheckedChange={(v) => setForm((f) => ({ ...f, IsSystem: v === true }))}
                                />
                                <Label htmlFor="cls-shipped" title={t('classifications.shippedHint', 'Ships with the product. Customers can see it but not change it.')}>
                                    {t('classifications.shippedLabel', 'Shipped classification')}
                                </Label>
                            </div>
                        )}
                        {/* WHO CAN EVEN BE ASSIGNED THIS. Set once against the kind of thing
                            rather than inferred per object: a Core Network Switch is recovered at
                            the rack because of what it is. Undecided is a real answer — the
                            assignment screen then reads the runbook wording and says it is
                            inferring, rather than defaulting to whichever value is cheaper. */}
                        <div className="space-y-1.5">
                            <Label htmlFor="cls-access">
                                {t('classifications.accessMode', 'Recovery access')}
                            </Label>
                            <Select
                                value={form.AccessMode ? String(form.AccessMode) : 'unset'}
                                onValueChange={(v) => setForm((f) => ({ ...f, AccessMode: v === 'unset' ? '' : v }))}
                            >
                                <SelectTrigger id="cls-access">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="unset">{t('classifications.accessUnset', 'Not decided')}</SelectItem>
                                    <SelectItem value="on-site">{t('classifications.accessOnSite', 'On-site — an engineer must be at the rack')}</SelectItem>
                                    <SelectItem value="remote">{t('classifications.accessRemote', 'Remote — recoverable over the network')}</SelectItem>
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground">
                                {t('classifications.accessHint',
                                    'On-site limits assignment to engineers at the object\u2019s site. Left undecided, the assignment screen infers it from the runbook and marks the answer as a guess.')}
                            </p>
                        </div>
                        <div className="flex justify-end gap-2 border-t pt-3">
                            <Button variant="outline" onClick={() => setShowForm(false)} disabled={saveMutation.isPending}>
                                {t('common.cancel', 'Cancel')}
                            </Button>
                            <Button
                                onClick={() => saveMutation.mutate()}
                                disabled={!form.Name.trim() || !form.Category.trim() || saveMutation.isPending}
                            >
                                {saveMutation.isPending ? t('common.saving', 'Saving…') : t('common.save', 'Save')}
                            </Button>
                        </div>
                    </div>
                </>
            )}
        </div>
    )
}
